import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { forwardRef, type ComponentProps, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../../../lib/utils.js";

export function DropdownMenu({
  dir,
  ...props
}: ComponentProps<typeof DropdownMenuPrimitive.Root>): React.JSX.Element {
  const { i18n } = useTranslation();
  const resolvedDir = dir ?? (i18n.dir() === "rtl" ? "rtl" : "ltr");
  return <DropdownMenuPrimitive.Root {...props} dir={resolvedDir} />;
}

export const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
export const DropdownMenuPortal = DropdownMenuPrimitive.Portal;

export const DropdownMenuContent = forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(function DropdownMenuContent({ className, style, ...props }, ref) {
  const { i18n } = useTranslation();
  const resolvedDir = i18n.dir() === "rtl" ? "rtl" : "ltr";
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        ref={ref}
        className={cn(
          "z-[var(--z-popover)] min-w-40 rounded-md border border-border bg-surface-elevated p-1 text-start shadow-lg animate-menu-in",
          className,
        )}
        style={{ direction: resolvedDir, ...style }}
        align="end"
        sideOffset={6}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  );
});
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

export function DropdownMenuItem({
  children,
  className,
  disabled,
  onSelect,
  "data-testid": dataTestId,
}: {
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  onSelect?: () => void;
  "data-testid"?: string;
}): React.JSX.Element {
  return (
    <DropdownMenuPrimitive.Item
        className={cn(
          "flex cursor-pointer items-center justify-start gap-2 rounded-sm px-2.5 py-1.5 text-start text-[0.8125rem] font-medium text-foreground outline-none data-[highlighted]:bg-surface-muted data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
          className,
        )}
      disabled={disabled}
      data-testid={dataTestId}
      onSelect={onSelect}
    >
      {children}
    </DropdownMenuPrimitive.Item>
  );
}

export const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

export function DropdownMenuRadioItem({
  value,
  children,
  className,
  "data-testid": dataTestId,
}: {
  value: string;
  children: ReactNode;
  className?: string;
  "data-testid"?: string;
}): React.JSX.Element {
  return (
    <DropdownMenuPrimitive.RadioItem
      value={value}
      className={cn(
        "flex cursor-pointer items-center justify-start gap-2 rounded-sm px-2.5 py-1.5 text-start text-[0.8125rem] font-medium text-foreground outline-none data-[highlighted]:bg-surface-muted",
        className,
      )}
      data-testid={dataTestId}
    >
      {children}
    </DropdownMenuPrimitive.RadioItem>
  );
}

export function DropdownMenuLabel({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): React.JSX.Element {
  return (
    <DropdownMenuPrimitive.Label
      className={cn(
        "px-3 py-2 text-[0.8rem] tracking-[0.04em] text-subtle uppercase",
        className,
      )}
    >
      {children}
    </DropdownMenuPrimitive.Label>
  );
}

export const DropdownMenuSub = DropdownMenuPrimitive.Sub;

export const DropdownMenuSubTrigger = forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger>
>(function DropdownMenuSubTrigger({ className, children, ...props }, ref) {
  return (
    <DropdownMenuPrimitive.SubTrigger
      ref={ref}
      className={cn(
        "flex cursor-pointer items-center justify-start gap-2 rounded-sm px-3 py-2 text-start text-[0.9rem] text-foreground outline-none data-[highlighted]:bg-surface-muted data-[state=open]:bg-surface-muted",
        className,
      )}
      {...props}
    >
      {children}
    </DropdownMenuPrimitive.SubTrigger>
  );
});

export const DropdownMenuSubContent = forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubContent>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent>
>(function DropdownMenuSubContent({ className, ...props }, ref) {
  const { i18n } = useTranslation();
  const resolvedDir = i18n.dir() === "rtl" ? "rtl" : "ltr";
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.SubContent
        ref={ref}
        className={cn(
          "z-[var(--z-popover)] min-w-44 rounded-md border border-border bg-surface-elevated p-1 text-start shadow-lg",
          className,
        )}
        style={{ direction: resolvedDir }}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  );
});

export function DropdownMenuSeparator({
  className,
}: {
  className?: string;
}): React.JSX.Element {
  return (
    <DropdownMenuPrimitive.Separator
      className={cn("mx-2 my-1 h-px bg-border", className)}
    />
  );
}
